import React, { useEffect, useRef, useState } from "react";
import TopBar from "@/components/TopBar";
import { api, apiUpload, fileUrl } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { FileText, Plus, ScanLine, Image as ImageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

import { swallow } from "@/lib/log";
import FavPinButtons from "@/components/FavPinButtons";
import RowActions from "@/components/RowActions";
import SwipeableRow from "@/components/SwipeableRow";
import MimeIcon from "@/components/MimeIcon";
import { useNavigate } from "react-router-dom";
import { Bell as BellIcon, CalendarClock } from "lucide-react";

const CATEGORY_COLORS = {
  passport:        "bg-[hsl(var(--info))]/12 text-[hsl(var(--info))]",
  aadhaar:         "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  pan:             "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  driving_licence: "bg-[hsl(var(--info))]/12 text-[hsl(var(--info))]",
  voter_id:        "bg-[hsl(var(--info))]/12 text-[hsl(var(--info))]",
  bill:            "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]",
  receipt:         "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]",
  medical:         "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
  insurance:       "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  certificate:     "bg-[hsl(var(--info))]/12 text-[hsl(var(--info))]",
  warranty:        "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]",
};

function humanSize(n) {
  if (!n && n !== 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Documents() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [imgPicker, setImgPicker] = useState(null); // {file} | null
  const inputRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/documents"); setItems(data); } catch (e) { swallow("Documents")(e); }
  };
  useEffect(() => { load(); }, []);

  // Contextual FAB event: trigger file picker
  useEffect(() => {
    const open = () => inputRef.current?.click();
    window.addEventListener("impo:docs-upload", open);
    return () => window.removeEventListener("impo:docs-upload", open);
  }, []);

  const uploadAs = async (file, kind /* 'document' | 'photo' */) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      if (kind === "photo") {
        await apiUpload("/photos", fd);
        toast.success("Saved to Photos");
        nav("/app/media?tab=photos");
      } else {
        // Documents are kept as-is (no OCR). Backend runs a lightweight
        // vision classifier best-effort to tag the category (aadhaar/pan/etc).
        const newDoc = await apiUpload("/documents", fd);
        // Optimistic add: the just-created doc goes to the top of the list
        // immediately, so the user sees it even if the SW served a stale
        // /api/documents snapshot on the follow-up refresh.
        if (newDoc && newDoc.id) {
          setItems((prev) => (prev.some((x) => x.id === newDoc.id) ? prev : [newDoc, ...prev]));
        }
        toast.success("Document saved");
        load(); // background reconciliation
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    // If it's an image, ask the user how they want to save it.
    if (file.type?.startsWith("image/")) {
      setImgPicker({ file });
      return;
    }
    // PDFs and everything else — straight to Documents.
    await uploadAs(file, "document");
  };

  // RowActions renders the delete-with-undo toast; here we just do the API + UI update.
  const del = async (d) => {
    setItems((arr) => arr.filter((x) => x.id !== d.id));
    try { await api.delete(`/documents/${d.id}`); }
    catch { toast.error("Delete failed — restored"); load(); }
  };

  return (
    <>
      <TopBar
        title="Documents"
        subtitle="Auto-categorized • Kept as-is"
        back
        showSearch
        right={
          <button onClick={() => inputRef.current?.click()} className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center" data-testid="docs-add-button">
            <Plus className="h-4 w-4" />
          </button>
        }
      />
      <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} data-testid="docs-file-input" />
      <div className="px-4 py-4 space-y-3">
        {uploading && (
          <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3 impo-in">
            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))] impo-shimmer" />
            <div className="flex-1">
              <div className="font-display font-semibold text-sm">Uploading document…</div>
              <div className="text-xs text-muted-foreground">Filed as-is — categorised automatically</div>
            </div>
          </div>
        )}
        {items.length === 0 && !uploading && (
          <EmptyState icon={ScanLine} title="No documents yet" subtitle="Snap a photo of a bill, passport or receipt — I'll file it." />
        )}
        {items.map((d) => {
          const sizeText = humanSize(d.size_bytes);
          const isImage = d.mime_type?.startsWith("image/");
          const isPdf = d.mime_type === "application/pdf";
          const shortName = d.file_path ? d.file_path.split("/").pop() : null;
          const secondaryLine = isPdf
            ? [`PDF`, d.page_count ? `${d.page_count} page${d.page_count === 1 ? "" : "s"}` : null, sizeText].filter(Boolean).join(" · ")
            : isImage
              ? [`Image`, d.mime_type?.split("/")[1]?.toUpperCase(), sizeText].filter(Boolean).join(" · ")
              : [shortName, sizeText].filter(Boolean).join(" · ");

          return (
            <SwipeableRow
              key={d.id}
              testId={`swipe-doc-${d.id}`}
              onSwipeLeft={async () => {
                setItems((arr) => arr.filter((x) => x.id !== d.id));
                try {
                  await api.post("/archive/toggle", { type: "document", id: d.id, archived: true });
                  toast.success("Document archived");
                } catch { toast.error("Archive failed"); load(); }
              }}
              onSwipeRight={async () => {
                const next = !d.is_favorite;
                setItems((arr) => arr.map((x) => x.id === d.id ? { ...x, is_favorite: next } : x));
                try {
                  await api.post("/favorites/toggle", { type: "document", id: d.id, is_favorite: next });
                  toast.success(next ? "Added to Favorites" : "Removed from Favorites");
                } catch { toast.error("Update failed"); load(); }
              }}
            >
              <div className="rounded-2xl border border-border bg-card p-3 flex gap-3" data-testid="document-item">
                <button onClick={() => setPreview(d)} className="h-16 w-16 rounded-xl overflow-hidden bg-muted flex items-center justify-center shrink-0">
                  {isImage && d.file_url ? (
                    <img
                      src={fileUrl(d.file_url)}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const parent = e.currentTarget.parentElement;
                        if (parent && !parent.dataset.fallback) {
                          parent.dataset.fallback = "1";
                          parent.innerHTML = '';
                          const div = document.createElement("div");
                          div.className = "h-full w-full grid place-items-center bg-purple-500/12 text-purple-500";
                          div.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                          parent.appendChild(div);
                        }
                      }}
                    />
                  ) : (
                    <MimeIcon mime={d.mime_type} />
                  )}
                </button>
                <button onClick={() => setPreview(d)} className="text-left flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-[10px] rounded-full ${CATEGORY_COLORS[d.category] || "bg-secondary text-secondary-foreground"}`}>
                      {d.category || "other"}
                    </Badge>
                    {(d.expiry_date || d.due_date) && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                        <CalendarClock className="h-3 w-3" />
                        {d.expiry_date ? `Exp ${d.expiry_date}` : `Due ${d.due_date}`}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-semibold text-sm truncate mt-1">{d.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{secondaryLine || " "}</div>
                  {(d.expiry_date || d.due_date) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const date = d.expiry_date || d.due_date;
                        const label = d.expiry_date ? "expires" : "is due";
                        const text = `${d.title} ${label} on ${date}`;
                        nav(`/app/reminders?new=1&text=${encodeURIComponent(text)}&due_at=${encodeURIComponent(date + "T09:00:00")}`);
                      }}
                      className="inline-flex items-center gap-1 mt-2 rounded-full border border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 px-2.5 py-1 text-[11px] font-medium"
                      data-testid={`doc-add-reminder-${d.id}`}
                    >
                      <BellIcon className="h-3 w-3" /> Add reminder
                    </button>
                  )}
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <FavPinButtons
                    type="document"
                    id={d.id}
                    isFavorite={!!d.is_favorite}
                    pinnedAt={d.pinned_at}
                    size="sm"
                    onChanged={(u) => setItems((arr) => arr.map((x) => x.id === d.id ? { ...x, ...u } : x))}
                    onArchived={(id) => setItems((arr) => arr.filter((x) => x.id !== id))}
                  />
                  <RowActions
                    size="sm"
                    testId={`doc-${d.id}`}
                    deleteLabel="Delete document"
                    onDelete={() => del(d)}
                  />
                </div>
              </div>
            </SwipeableRow>
          );
        })}
      </div>

      {/* Preview dialog — stripped of OCR display since we no longer run OCR by default. */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display truncate">{preview?.title}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-[10px] rounded-full ${CATEGORY_COLORS[preview.category] || "bg-secondary text-secondary-foreground"}`}>
                  {preview.category || "other"}
                </Badge>
                {(preview.expiry_date || preview.due_date) && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                    <CalendarClock className="h-3 w-3" />
                    {preview.expiry_date ? `Exp ${preview.expiry_date}` : `Due ${preview.due_date}`}
                  </span>
                )}
              </div>

              {preview.mime_type?.startsWith("image/") ? (
                <img src={fileUrl(preview.file_url)} alt="" className="w-full rounded-xl border border-border" />
              ) : preview.mime_type === "application/pdf" ? (
                <a
                  href={fileUrl(preview.file_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 hover:bg-[hsl(var(--accent))]"
                  data-testid="doc-preview-open-pdf"
                >
                  <FileText className="h-6 w-6 text-primary" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">Open PDF</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {[preview.page_count && `${preview.page_count} pages`, humanSize(preview.size_bytes)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </a>
              ) : (
                <a
                  href={fileUrl(preview.file_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 hover:bg-[hsl(var(--accent))]"
                  data-testid="doc-preview-open-file"
                >
                  <FileText className="h-6 w-6 text-primary" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">Open file</div>
                    <div className="text-[11px] text-muted-foreground truncate">{preview.mime_type || "file"} · {humanSize(preview.size_bytes) || ""}</div>
                  </div>
                </a>
              )}

              {preview.ocr_text && (
                <>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mt-2">Extracted text</div>
                  <div className="text-sm max-h-[40vh] overflow-y-auto whitespace-pre-wrap p-3 rounded-xl bg-muted">
                    {preview.ocr_text}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image upload picker: user chose an image — ask if it should be a document or a photo. */}
      <Dialog open={!!imgPicker} onOpenChange={(v) => !v && setImgPicker(null)}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">How should we save this?</DialogTitle>
            <DialogDescription>
              You picked an image. Documents are auto-categorised (Aadhaar, PAN, receipts, etc.). Photos live in your gallery.
            </DialogDescription>
          </DialogHeader>
          {imgPicker?.file && (
            <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <div className="h-14 w-14 rounded-lg overflow-hidden bg-muted grid place-items-center">
                <img
                  src={URL.createObjectURL(imgPicker.file)}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{imgPicker.file.name}</div>
                <div className="text-[11px] text-muted-foreground">{humanSize(imgPicker.file.size)}</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button
              variant="outline"
              className="h-14 gap-1.5 flex-col"
              onClick={() => { const f = imgPicker.file; setImgPicker(null); uploadAs(f, "photo"); }}
              data-testid="doc-picker-save-photo"
            >
              <ImageIcon className="h-4 w-4" />
              <span className="text-xs">Save as Photo</span>
            </Button>
            <Button
              className="h-14 gap-1.5 flex-col"
              onClick={() => { const f = imgPicker.file; setImgPicker(null); uploadAs(f, "document"); }}
              data-testid="doc-picker-save-doc"
            >
              <FileText className="h-4 w-4" />
              <span className="text-xs">Save as Document</span>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImgPicker(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
